import type { OrderedMap } from 'immutable';
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import VirtualList from 'react-tiny-virtual-list-dmk';
import { useUpdate } from 'react-use';

import { featureFlagsActions, featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { localPreferences } from 'app-components/utils/core';

import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockIcon from '@mui/icons-material/Lock';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import type { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';

import { entitlementsActions, entitlementsSelectors, entitlementsTypes } from 'app-components/flux/entitlements';
import HighlightText from 'components/HighlightText';
import { dispatchSimpleNotification } from 'data/notifications/notificationsUtils';

const itemSize = 62;

const FFMenu = (props: MenuProps) => {
  const dispatch = useDispatch();
  const update = useUpdate();

  const featureFlags: OrderedMap<string, any> = useSelector(featureFlagsSelectors.getFeatureFlags);
  const [featureFlagFilter, setFeatureFlagFilter] = useState<string>('');
  const entitlement: any = useSelector(entitlementsSelectors.getTopTierEntitlement);
  const bizEnabled = useSelector(entitlementsSelectors.getBusinessEnabled);
  const [businessEnabled, setBusinessEnabled] = useState<boolean>(bizEnabled);
  const featureFlagsOverrides = localPreferences.getItem('featureFlagsOverrides') || null;

  const featureFlagsProcessed = useMemo(
    () =>
      featureFlags
        .filter(
          (value, key) =>
            !featureFlagFilter ||
            Object.prototype.hasOwnProperty.call(featureFlagsOverrides || {}, key) ||
            key.toLowerCase().includes(featureFlagFilter),
        )
        .sortBy(
          (value, key) => key,
          (key1, key2) => {
            const override1 = featureFlagsOverrides?.[key1] !== undefined ? 0 : 1;
            const override2 = featureFlagsOverrides?.[key2] !== undefined ? 0 : 1;
            return override1 === override2 ? key1.localeCompare(key2) : override1 - override2;
          },
        )
        .keySeq(),
    [featureFlags, featureFlagFilter, featureFlagsOverrides],
  );

  const itemsCount = featureFlagsProcessed?.size || 0;

  return (
    <Menu
      key='feature-flags-menu'
      style={{
        ...props.style,
      }}
      MenuListProps={{
        style: {
          height: '80vh',
          width: 500,
          ...props.MenuListProps?.style,
        },
        ...props.MenuListProps,
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id='feature-flags-menu'
      {...props}
    >
      <MenuItem dense disableGutters key='searchbar'>
        <TextField
          size='small'
          hiddenLabel
          placeholder='Search Flags'
          type='search'
          fullWidth
          autoFocus
          autoComplete='flag-search'
          onChange={(event) => {
            setFeatureFlagFilter(event.target.value.toLowerCase());
          }}
          onKeyDown={(event) => event.stopPropagation()}
          onKeyUp={(event) => event.stopPropagation()}
        />
      </MenuItem>

      <MenuItem dense disableGutters>
        <ListItemButton
          sx={{ flexGrow: 0 }}
          onClick={() => {
            if (Object.prototype.hasOwnProperty.call(featureFlagsOverrides || {}, 'business-tier')) {
              delete featureFlagsOverrides['business-tier'];
              localPreferences.setItem('featureFlagsOverrides', featureFlagsOverrides);
            } else {
              localPreferences.setItem('featureFlagsOverrides', {
                ...featureFlagsOverrides,
                ['business-tier']: true,
              });
            }
            update();
          }}
        >
          {featureFlagsOverrides?.['business-tier'] !== undefined ? (
            <LockIcon fontSize='small' color='primary' />
          ) : (
            <LockOpenOutlinedIcon fontSize='small' />
          )}
        </ListItemButton>
        <ListItemText primary='Quicken Business and Personal' />
        <Switch
          size='small'
          color='primary'
          onChange={() => {
            // TODO: fix this
            setBusinessEnabled((v) => {
              dispatch(
                entitlementsActions.createEntitlementSuccess({
                  replaceCollection: true,
                  resources: [
                    entitlementsTypes.mkEntitlement({
                      ...entitlement?.toJS(),
                      id: !v ? 'ACME_HAB' : 'ACME_BASIC',
                    }),
                  ],
                }),
              );
              return !v;
            });
          }}
          checked={businessEnabled}
        />
      </MenuItem>

      <Divider />

      <VirtualList
        itemCount={itemsCount}
        itemSize={itemSize}
        height={13 * itemSize}
        overscanCount={20}
        renderItem={({ index, style }) => {
          const key = featureFlagsProcessed.get(index);
          return !key ? null : (
            <MenuItem
              dense
              disableGutters
              key={key}
              style={style}
              selected={Object.prototype.hasOwnProperty.call(featureFlagsOverrides || {}, key)}
            >
              <ListItemButton
                sx={{ flexGrow: 0 }}
                onClick={() => {
                  if (Object.prototype.hasOwnProperty.call(featureFlagsOverrides || {}, key)) {
                    delete featureFlagsOverrides[key];
                    localPreferences.setItem('featureFlagsOverrides', featureFlagsOverrides);
                  } else {
                    localPreferences.setItem('featureFlagsOverrides', {
                      ...featureFlagsOverrides,
                      [key]: featureFlags.get(key),
                    });
                  }
                  update();
                }}
              >
                {featureFlagsOverrides?.[key] !== undefined ? (
                  <LockIcon fontSize='small' color='primary' />
                ) : (
                  <LockOpenOutlinedIcon fontSize='small' />
                )}
              </ListItemButton>
              <ListItemText
                style={{ marginRight: 8 }}
                primary={<HighlightText text={key} searchKeys={[featureFlagFilter]} />}
                onClick={() => {
                  navigator.clipboard.writeText(key);
                  dispatchSimpleNotification('key copied to clipboard');
                }}
              />
              <TextField
                size='small'
                margin='dense'
                hiddenLabel
                style={{ maxWidth: 96 }}
                value={String(featureFlags.get(key))}
                onChange={(event) => {
                  dispatch(
                    featureFlagsActions.applyFeatureFlagsChanges({
                      [key]: event.target.value,
                    }),
                  );
                  if (Object.prototype.hasOwnProperty.call(featureFlagsOverrides || {}, key)) {
                    localPreferences.setItem('featureFlagsOverrides', {
                      ...featureFlagsOverrides,
                      [key]: String(event.target.value),
                    });
                  }
                }}
                onKeyDown={(event) => event.stopPropagation()}
                onKeyUp={(event) => event.stopPropagation()}
              />
              <Switch
                size='small'
                color='primary'
                onChange={(event, value) => {
                  dispatch(
                    featureFlagsActions.applyFeatureFlagsChanges({
                      [key]: value,
                    }),
                  );
                  if (Object.prototype.hasOwnProperty.call(featureFlagsOverrides || {}, key)) {
                    localPreferences.setItem('featureFlagsOverrides', {
                      ...featureFlagsOverrides,
                      [key]: value,
                    });
                  }
                }}
                checked={Boolean(featureFlags.get(key))}
              />
            </MenuItem>
          );
        }}
      />
    </Menu>
  );
};
export default React.memo(FFMenu);
